import { PlatformAssessment } from "@pandas/http-client";
import { Progress } from "../src/types";
import { GetAssessmentInstance } from "./assessmentComponents/assessmentFactory";
import { Assessment } from "./assessmentComponents/assessmentTypes";
import {
	AssessmentComponent,
	FunctionalComponent,
	functionalComponents,
	GraderCosmeticComponent,
	graderComponents,
	SelfComponent,
	selfComponents,
} from "./assessmentComponents/componentTypes";
import { ComponentConditionKey, ComponentConditionValues } from "./assessmentComponents/conditions";
import { ComponentVersion } from "./assessmentEngineConfig";
import { postAssessments } from "./services/platform/platform-service";
import { AssessmentStateEntry, AssessmentsEntry } from "./types";

function notNull<T>(val: T | null): val is T {
	return val !== null;
}

export class AssessmentEngine {
	Assessments: Array<Assessment>;
	constructor(supportedAssessments: AssessmentsEntry[], componentVersion: ComponentVersion) {
		this.Assessments = supportedAssessments
			.flatMap((a) => a.components.map((c) => GetAssessmentInstance(a.key, a.id, c, componentVersion)))
			.filter(notNull);
	}
	public ResetState() {
		this.Assessments.forEach((a) => (a.Condition = undefined));
	}
	public ResetFunctionalAssessments() {
		this.Assessments.filter((a) => functionalComponents.includes(a.ComponentKey as FunctionalComponent)).forEach(
			(a) => (a.Condition = undefined),
		);
	}
	public ResetSelfAssessments() {
		this.Assessments.filter((a) => selfComponents.includes(a.ComponentKey as SelfComponent)).forEach(
			(a) => (a.Condition = undefined),
		);
	}
	public ResetGraderAssessments() {
		this.Assessments.filter((a) => graderComponents.includes(a.ComponentKey as GraderCosmeticComponent)).forEach(
			(a) => (a.Condition = undefined),
		);
	}
	public GetByKey(assessmentName: AssessmentComponent): Assessment {
		const assessment = this.Assessments.find((a) => a.ComponentKey === assessmentName);
		if (assessment) return assessment;
		throw new Error(`assessment of type ${assessmentName} was not found or not configured`);
	}
	public GetAllByKey(assessmentName: AssessmentComponent): Assessment[] {
		const assessments = this.Assessments.filter((a) => a.ComponentKey === assessmentName);
		if (assessments.length > 0) return assessments;
		throw new Error(`Assessments of type ${assessmentName} were not found or not configured`);
	}
	public GetBatchByKeysSorted(assessmentNames: AssessmentComponent[]): Assessment[] {
		const assessments = this.Assessments.filter((a) => assessmentNames.includes(a.ComponentKey)).sort(
			(a, b) => assessmentNames.indexOf(a.ComponentKey) - assessmentNames.indexOf(b.ComponentKey),
		);
		return assessments;
	}
	public SetCondition(assessmentName: AssessmentComponent, condition: ComponentConditionValues) {
		this.GetByKey(assessmentName).Condition = condition;
	}
	public GetNextAssessment() {
		this.Assessments?.find((a) => !a.Condition);
	}
	public GetProgress(): Progress {
		const completedSteps = this.Assessments.reduce((n, item) => (item.Condition !== undefined ? n + 1 : n), 0);
		return new Progress(this.Assessments.length, completedSteps);
	}
	public async AddAssessmentsToPlatform(sessionId: string, uploadSkippedAsFailed: boolean = false) {
		const filteredAssessments = uploadSkippedAsFailed ? this.Assessments : this.Assessments.filter((a) => a.Condition);
		const platformAssessments = filteredAssessments.map<PlatformAssessment>((a) => ({
			assessmentTypeId: a.AssessmentId,
			result: a.Condition ? "passed" : "failed",
			componentConditionId: a.Condition?.Id ?? a.GetConditionByBoolean(false).Id,
		}));
		console.log(filteredAssessments.map((a) => `${a.ComponentKey} - ${a.Condition?.Name}`));
		await postAssessments(platformAssessments, sessionId);
	}
	public ExportState(): AssessmentStateEntry[] {
		return this.Assessments.filter((a) => a.Condition).map<AssessmentStateEntry>((a) => ({
			assessmentKey: a.ComponentKey,
			componentName: a.ComponentName,
			condition: a.Condition?.Name as ComponentConditionKey,
		}));
	}
	public ImportAndAttachToState(
		state: AssessmentStateEntry[],
		onStateUpdate: (state: AssessmentStateEntry[]) => void = () => {},
	) {
		state.forEach((e) => {
			this.Assessments.forEach((assessment) => {
				if (assessment.ComponentKey === e.assessmentKey && assessment.ComponentName === e.componentName) {
					assessment.SetConditionByKey(e.condition);
				}
			});
		});

		this.Assessments.forEach((a) => {
			a.onConditionUpdate = (updatedAssessment: Assessment) => {
				if (!updatedAssessment.Condition) return;

				const existingEntry = state.find(
					(e) =>
						e.assessmentKey === updatedAssessment.ComponentKey && e.componentName === updatedAssessment.ComponentName,
				);

				if (!existingEntry) {
					state.push({
						assessmentKey: updatedAssessment.ComponentKey,
						componentName: updatedAssessment.ComponentName,
						condition: updatedAssessment.Condition.Name as ComponentConditionKey,
					});
				} else {
					existingEntry.condition = updatedAssessment.Condition.Name as ComponentConditionKey;
				}

				onStateUpdate(state);
			};
		});
	}
}
